import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { SetPasswordModal, ChangeOwnPassword } from "../components/AdminPasswordTools";

export default function Admin() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [userTracks, setUserTracks] = useState([]);
  const [tab, setTab] = useState("users");
  const [jobs, setJobs] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [resetBusy, setResetBusy] = useState(false);
  const [setPwTarget, setSetPwTarget] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/auth"); return; }
    if (!user.is_admin) { nav("/dashboard"); return; }
  }, [loading, user, nav]);

  const loadAll = useCallback(async () => {
    try {
      const [s, u] = await Promise.all([api.get("/admin/stats"), api.get("/admin/users")]);
      setStats(s.data);
      setUsers(u.data);
    } catch (e) {
      toast.error("Failed to load admin data");
    }
  }, []);

  useEffect(() => { if (user?.is_admin) loadAll(); }, [user, loadAll]);

  const loadJobs = useCallback(async () => {
    try {
      const r = await api.get("/admin/import-jobs");
      setJobs(r.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { if (tab === "jobs" && user?.is_admin) loadJobs(); }, [tab, user, loadJobs]);

  const loadAudit = useCallback(async () => {
    try {
      const r = await api.get("/admin/audit-log");
      setAuditLog(r.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { if (tab === "audit" && user?.is_admin) loadAudit(); }, [tab, user, loadAudit]);

  const openUser = async (u) => {
    setSelectedUser(u);
    try {
      const r = await api.get(`/admin/users/${u.id}/tracks`);
      setUserTracks(r.data);
    } catch {
      setUserTracks([]);
    }
  };

  const grantPro = async (u) => {
    if (!window.confirm(`Grant Pro (active) to ${u.email}?`)) return;
    await api.post(`/admin/users/${u.id}/grant-pro`);
    toast.success(`${u.email} is now Pro`);
    loadAll();
    if (tab === "audit") loadAudit();
    if (selectedUser?.id === u.id) openUser({ ...u, subscription_status: "active" });
  };

  const revokePro = async (u) => {
    if (!window.confirm(`Revoke Pro from ${u.email}? They'll be on Free.`)) return;
    await api.post(`/admin/users/${u.id}/revoke-pro`);
    toast.success(`${u.email} downgraded to Free`);
    loadAll();
    if (tab === "audit") loadAudit();
  };

  const deleteUser = async (u) => {
    if (u.is_admin) { toast.error("Cannot delete an admin"); return; }
    const a = window.prompt(`Type DELETE to permanently remove ${u.email} and all their data:`);
    if (a !== "DELETE") return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User deleted");
      setSelectedUser(null);
      setUserTracks([]);
      loadAll();
      if (tab === "audit") loadAudit();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const confirmResetPassword = async () => {
    if (!resetTarget) return;
    setResetBusy(true);
    try {
      const r = await api.post(`/admin/users/${resetTarget.id}/reset-password`);
      setResetResult(r.data);
      toast.success("Password reset · sessions invalidated");
      if (tab === "audit") loadAudit();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
      setResetTarget(null);
    } finally {
      setResetBusy(false);
    }
  };

  const closeResetModal = () => {
    setResetTarget(null);
    setResetResult(null);
  };

  const filtered = users.filter((u) =>
    !search.trim() || u.email.toLowerCase().includes(search.toLowerCase())
  );

  if (loading || !user?.is_admin) return null;

  return (
    <>
      <Header />
      <div className="lq-container page-enter" data-testid="admin-page" style={{ maxWidth: 1200 }}>
        <div className="eyebrow" style={{ color: "var(--accent)" }}>— Admin Console</div>
        <h1 className="serif" style={{ fontSize: 56, lineHeight: 0.95, margin: "10px 0 24px", letterSpacing: "-0.02em" }}>
          Operations
        </h1>

        {/* Stats */}
        {stats && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginBottom: 28, paddingBottom: 24, borderBottom: "1px solid var(--border-soft)" }} data-testid="admin-stats">
            <Stat n={stats.total_users} label="Users" />
            <Stat n={stats.total_tracks} label="Tracks" />
            <Stat n={stats.active_subscriptions} label="Active Pro" accent />
            <Stat n={stats.trialing} label="Trialing" />
            <Stat n={stats.free} label="Free" />
            <Stat n={`$${stats.mrr_estimate_usd}`} label="MRR (est)" accent serif />
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: "flex", gap: 8, marginBottom: 22, borderBottom: "1px solid var(--border-soft)", paddingBottom: 0 }}>
          <TabBtn active={tab === "users"} onClick={() => setTab("users")} testid="tab-users">Users</TabBtn>
          <TabBtn active={tab === "jobs"} onClick={() => setTab("jobs")} testid="tab-jobs">Import Jobs</TabBtn>
          <TabBtn active={tab === "audit"} onClick={() => setTab("audit")} testid="tab-audit">Audit Log</TabBtn>
          <TabBtn active={tab === "account"} onClick={() => setTab("account")} testid="tab-account">My Account</TabBtn>
        </div>

        {tab === "users" && (
          <div className={`admin-users-grid ${selectedUser ? "has-detail" : ""}`}>
            <div>
              <input
                className="lq-input"
                placeholder="Search users by email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ marginBottom: 14 }}
                data-testid="admin-user-search"
              />
              <div data-testid="admin-users-list">
                {filtered.length === 0 && <div className="callout">No users yet.</div>}
                {filtered.map((u) => (
                  <UserRow key={u.id}
                    u={u}
                    selected={selectedUser?.id === u.id}
                    onClick={() => openUser(u)}
                  />
                ))}
              </div>
            </div>

            {selectedUser && (
              <div className="admin-user-detail">
                <div className="settings-section">
                  <div className="settings-section-head">
                    <div className="settings-section-icon">⊕</div>
                    <div className="settings-section-title">User · {selectedUser.email}</div>
                  </div>
                  <div className="settings-section-body">
                    <DetailRow label="ID" value={selectedUser.id} mono />
                    <DetailRow label="Status" value={selectedUser.subscription_status} />
                    <DetailRow label="Plan" value={selectedUser.subscription_plan || "—"} />
                    <DetailRow label="Tracks" value={selectedUser.track_count} />
                    <DetailRow label="Signed up" value={selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleString() : "—"} />
                    <DetailRow label="Last login" value={selectedUser.last_login ? new Date(selectedUser.last_login).toLocaleString() : "—"} />

                    <div style={{ marginTop: 16, display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {selectedUser.subscription_status !== "active" && (
                        <button className="connection-btn primary" onClick={() => grantPro(selectedUser)} data-testid="grant-pro-btn">
                          Grant Pro
                        </button>
                      )}
                      {(selectedUser.subscription_status === "active" || selectedUser.subscription_status === "trialing") && (
                        <button className="connection-btn" onClick={() => revokePro(selectedUser)} data-testid="revoke-pro-btn">
                          Revoke Pro
                        </button>
                      )}
                      {selectedUser.id !== user.id && (
                        <button className="connection-btn" onClick={() => { setResetTarget(selectedUser); setResetResult(null); }} data-testid="reset-password-btn">
                          Reset Password
                        </button>
                      )}
                      {selectedUser.id !== user.id && (
                        <button className="connection-btn" onClick={() => setSetPwTarget(selectedUser)} data-testid="set-password-btn">
                          Set Password
                        </button>
                      )}
                      {!selectedUser.is_admin && (
                        <button className="connection-btn danger" onClick={() => deleteUser(selectedUser)} data-testid="delete-user-btn">
                          Delete User
                        </button>
                      )}
                    </div>

                    <div style={{ marginTop: 18 }}>
                      <div className="eyebrow" style={{ marginBottom: 8 }}>— Catalog ({userTracks.length})</div>
                      <div style={{ maxHeight: 340, overflowY: "auto", border: "1px solid var(--border-soft)", borderRadius: 4 }}>
                        {userTracks.length === 0 && <div className="data-line" style={{ padding: 14 }}>No tracks</div>}
                        {userTracks.map((t) => (
                          <div key={t.id} style={{ padding: "10px 12px", borderBottom: "1px solid var(--border-soft)", fontSize: 13 }}>
                            <div className="serif" style={{ fontSize: 16 }}>{t.title || "(untitled)"}</div>
                            <div className="data-line">
                              {t.status?.toUpperCase()}{t.isrc ? ` · ${t.isrc}` : ""}{t.release_date ? ` · ${t.release_date}` : ""}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "account" && <ChangeOwnPassword email={user.email} />}

        {tab === "jobs" && (
          <div data-testid="admin-jobs-list">
            {jobs.length === 0 && <div className="callout">No import jobs yet.</div>}
            {jobs.map((j) => (
              <div key={j.id} style={{ padding: "14px 0", borderBottom: "1px solid var(--border-soft)", display: "grid", gridTemplateColumns: "1fr auto", gap: 12 }}>
                <div>
                  <div className="serif" style={{ fontSize: 18 }}>
                    {j.status === "done" ? "✓" : j.status === "failed" ? "✕" : "•"} {j.user_id?.slice(0, 8)}…
                  </div>
                  <div className="data-line">
                    {j.status?.toUpperCase()} · {j.progress_count}/{j.total_count}{j.current_track_name ? ` · ${j.current_track_name}` : ""}{j.error_message ? ` · ${j.error_message}` : ""}
                  </div>
                </div>
                <div className="data-line">{j.started_at ? new Date(j.started_at).toLocaleString() : ""}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "audit" && (
          <div data-testid="admin-audit-log">
            {auditLog.length === 0 && <div className="callout">No admin actions logged yet.</div>}
            {auditLog.map((entry) => {
              const actionStyle = ACTION_STYLES[entry.action] || { color: "var(--text-mute)", border: "var(--border)", bg: "transparent", label: entry.action.toUpperCase().replace(/_/g, " ") };
              return (
                <div key={entry.id} className="audit-row">
                  <span className="mono" style={{
                    fontSize: 10, letterSpacing: "0.14em", padding: "4px 10px",
                    border: `1px solid ${actionStyle.border}`, borderRadius: 999,
                    color: actionStyle.color, background: actionStyle.bg,
                  }}>
                    {actionStyle.label}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, color: "var(--text)" }}>
                      <strong style={{ fontWeight: 500 }}>{entry.admin_email}</strong>
                      {entry.target_email && (
                        <> → <span style={{ color: "var(--text-mute)" }}>{entry.target_email}</span></>
                      )}
                    </div>
                    {entry.metadata && Object.keys(entry.metadata).length > 0 && (
                      <div className="data-line">
                        {Object.entries(entry.metadata).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                      </div>
                    )}
                  </div>
                  <div className="data-line">{new Date(entry.timestamp).toLocaleString()}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {setPwTarget && (
        <SetPasswordModal target={setPwTarget} onClose={() => setSetPwTarget(null)} onDone={() => tab === "audit" && loadAudit()} />
      )}

      {/* Password reset confirmation modal */}
      {resetTarget && (
        <div className="modal-backdrop" onClick={closeResetModal} data-testid="reset-password-modal">
          <div className="modal-card" style={{ maxWidth: 480, padding: 28 }} onClick={(e) => e.stopPropagation()}>
            {!resetResult ? (
              <>
                <div className="eyebrow" style={{ color: "var(--red-light)" }}>— Admin Action</div>
                <h2 className="serif" style={{ fontSize: 30, lineHeight: 1.05, margin: "8px 0 12px" }}>
                  Reset password for {resetTarget.email}?
                </h2>
                <p style={{ color: "var(--text-mute)", fontSize: 14, lineHeight: 1.6, marginBottom: 22 }}>
                  This will generate a temporary password and invalidate all of their active sessions. They&apos;ll have to sign in again with the temp password and change it.
                </p>
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <button className="btn btn-ghost" onClick={closeResetModal} data-testid="reset-cancel-btn">Cancel</button>
                  <button className="btn btn-danger" onClick={confirmResetPassword} disabled={resetBusy} data-testid="reset-confirm-btn">
                    {resetBusy ? "Resetting…" : "Reset Password"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{
                  width: 48, height: 48, borderRadius: "50%",
                  background: "rgba(21, 128, 61, 0.18)", color: "var(--green-light)",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  fontSize: 24, marginBottom: 14,
                }}>✓</div>
                <div className="eyebrow" style={{ color: "var(--green-light)" }}>— Reset Complete</div>
                <h2 className="serif" style={{ fontSize: 28, lineHeight: 1.05, margin: "8px 0 12px" }}>
                  Temporary password issued
                </h2>
                <p style={{ color: "var(--text-mute)", fontSize: 13.5, marginBottom: 14 }}>
                  Email service is not configured. Share this temp password with <strong style={{ color: "var(--text)" }}>{resetResult.email}</strong> through a secure channel. All their existing sessions have been invalidated.
                </p>
                <div style={{
                  background: "var(--bg-elev-2)", padding: 14, borderRadius: 4,
                  border: "1px solid var(--border)", marginBottom: 18,
                }}>
                  <div className="label" style={{ marginBottom: 6 }}>Temporary Password</div>
                  <div
                    className="mono"
                    style={{ fontSize: 16, color: "var(--accent)", letterSpacing: "0.04em", wordBreak: "break-all" }}
                    data-testid="temp-password-display"
                  >
                    {resetResult.temp_password}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <button
                    className="btn btn-light"
                    onClick={() => { navigator.clipboard.writeText(resetResult.temp_password); toast.success("Copied"); }}
                    data-testid="copy-temp-pw-btn"
                  >
                    Copy
                  </button>
                  <button className="btn btn-primary" onClick={closeResetModal} data-testid="reset-done-btn">Done</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function Stat({ n, label, accent, serif }) {
  return (
    <div>
      <div className={`stat-num ${accent ? "accent" : ""}`} style={{ fontSize: 36 }}>
        {typeof n === "number" ? String(n).padStart(2, "0") : n}
      </div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      style={{
        padding: "10px 16px",
        background: "transparent",
        color: active ? "var(--accent)" : "var(--text-mute)",
        border: 0,
        borderBottom: `2px solid ${active ? "var(--accent)" : "transparent"}`,
        cursor: "pointer",
        fontFamily: "var(--mono)",
        fontSize: 11,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        marginBottom: -1,
      }}
    >
      {children}
    </button>
  );
}

function UserRow({ u, selected, onClick }) {
  const badgeClass =
    u.subscription_status === "active" ? "active" :
    u.subscription_status === "trialing" ? "trial" :
    u.subscription_status === "past_due" ? "past_due" :
    u.subscription_status === "canceled" ? "canceled" : "free";
  return (
    <div
      onClick={onClick}
      data-testid={`admin-user-row-${u.id}`}
      style={{
        padding: "14px 14px",
        borderBottom: "1px solid var(--border-soft)",
        cursor: "pointer",
        background: selected ? "rgba(217, 119, 6, 0.06)" : "transparent",
        display: "grid",
        gridTemplateColumns: "1fr auto auto",
        gap: 14,
        alignItems: "center",
        transition: "background-color 0.12s ease",
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {u.email}{u.is_admin && <span style={{ marginLeft: 8, color: "var(--accent)", fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.10em" }}>· ADMIN</span>}
        </div>
        <div className="data-line">
          {u.track_count} track{u.track_count === 1 ? "" : "s"}
          {u.created_at && ` · ${new Date(u.created_at).toLocaleDateString()}`}
        </div>
      </div>
      <span className={`sub-badge ${badgeClass}`}>{u.subscription_status}</span>
      <span style={{ color: "var(--text-fade)" }}>›</span>
    </div>
  );
}

function DetailRow({ label, value, mono }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 12, padding: "8px 0", borderBottom: "1px solid var(--border-soft)" }}>
      <div className="label" style={{ marginBottom: 0 }}>{label}</div>
      <div style={{ fontFamily: mono ? "var(--mono)" : "var(--sans)", fontSize: mono ? 12 : 13.5, color: "var(--text)", wordBreak: "break-all" }}>
        {value}
      </div>
    </div>
  );
}

const ACTION_STYLES = {
  grant_pro:      { label: "GRANT PRO",      color: "var(--green-light)", border: "rgba(21,128,61,0.40)",  bg: "rgba(21,128,61,0.10)" },
  revoke_pro:     { label: "REVOKE PRO",     color: "var(--accent)",      border: "rgba(217,119,6,0.40)",  bg: "rgba(217,119,6,0.10)" },
  delete_user:    { label: "DELETE USER",    color: "var(--red-light)",   border: "rgba(159,18,57,0.40)",  bg: "rgba(159,18,57,0.10)" },
  set_password:   { label: "SET PASSWORD",   color: "var(--red-light)",   border: "rgba(159,18,57,0.40)",  bg: "rgba(159,18,57,0.10)" },
  password_reset: { label: "PASSWORD RESET", color: "var(--red-light)",   border: "rgba(159,18,57,0.40)",  bg: "rgba(159,18,57,0.10)" },
};
